import { isCrossAppLink } from 'Molecules/NotificationLink/NotificationLink';

/**
 * Safety bound on how long a cross-app notification click waits before
 * navigating anyway. The keepalive read (see `markReadBeacon`) resolves as soon
 * as the request is *dispatched* — normally a few ms — so this only fires if
 * obtaining the auth token hangs, ensuring a stuck token refresh can never trap
 * the user on the bell dropdown.
 */
export const MARK_READ_NAV_TIMEOUT_MS = 2000;

interface HandleNotificationClickParams {
  event: { preventDefault: () => void };
  link?: string;
  /**
   * In-app fire-and-forget read (redux dispatch). Used for SPA links, where no
   * document unload occurs so the request completes on its own.
   */
  markRead: () => unknown;
  /**
   * Cross-app read via a keepalive request that survives the page unload.
   * Resolves once the request is dispatched, NOT when the response returns — so
   * navigation can fire immediately. Used for `/v2` links only.
   */
  markReadBeacon: () => Promise<void>;
  /** Performs a full-page navigation to the cross-app link. */
  navigate: (link: string) => void;
  timeoutMs?: number;
}

/**
 * Marks a notification read on click and routes the user to the target.
 *
 * A `/v2` link renders as a native `<a href>` whose default action tears the
 * document down, which would abort an in-flight XHR read. We `preventDefault()`,
 * fire the read as a keepalive request (`markReadBeacon`) that survives the
 * unload, then navigate as soon as that request is dispatched — so the click
 * feels instant while the read still persists. A timeout bounds the wait so a
 * hung token refresh can't trap the user.
 *
 * In-app links render as react-router `<Link>`s — no document unload occurs, so
 * the existing fire-and-forget redux read (`markRead`) completes on its own and
 * we leave SPA navigation untouched.
 */
export const handleNotificationClick = async ({
  event,
  link,
  markRead,
  markReadBeacon,
  navigate,
  timeoutMs = MARK_READ_NAV_TIMEOUT_MS,
}: HandleNotificationClickParams): Promise<void> => {
  if (!isCrossAppLink(link)) {
    markRead();
    return;
  }

  event.preventDefault();

  await Promise.race([
    markReadBeacon().catch(() => undefined),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);

  navigate(link);
};
